function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const e = error as { statusCode?: number, status?: number }
  return e.statusCode ?? e.status
}

function useAuthReady() {
  const { user, loggedIn } = useUserSession()

  // No expiresAt check: that timestamp only refreshes during SSR, so it goes
  // stale in a long-lived tab. The metadata routes call ensureFreshToken.
  const isAuthReady = computed(() => loggedIn.value && !!user.value?.userId)

  return { user, isAuthReady }
}

export function usePersonalization<T = unknown>(key: string) {
  // `T` is not validation — data comes from user-controlled storage, so callers must still parse/validate it (see useFavorites/useLastActivity).
  const { user, isAuthReady } = useAuthReady()
  const queryClient = useQueryClient()

  const queryKey = computed(() => [user.value?.userId, 'personalization', key])

  // Avoid Nuxt typed-route matching here. Typing the path as `string` prevents the typescript deep-instantiation error
  const metadataUrl: string = `/api/metadata/${key}`

  const query = useQuery({
    queryKey,
    enabled: computed(() => import.meta.client && isAuthReady.value),
    retry: false,
    queryFn: async ({ signal }) => {
      try {
        const data = await $fetch<T>(metadataUrl, { signal })
        // Tanstack Query throws an error if queryFn returns undefined, so coerce an empty body to null.
        // Otherwise, users without favorites won't be able to use personalization features.
        return data ?? null
      }
      catch (error: unknown) {
        if (getErrorStatus(error) === 404) {
          return null
        }
        throw error
      }
    },
  })

  const mutation = useMutation({
    retry: false,
    mutationFn: async ({ data, targetUserId }: { data: T, previous: T | null | undefined, targetUserId: string }) => {
      // Queued for one user, another is signed in now: the write belongs to a
      // session that no longer exists and must not land on the current user.
      if (targetUserId !== user.value?.userId) {
        throw new Error('Cannot set metadata: the signed-in user changed')
      }

      return await $fetch<T>(metadataUrl, {
        method: 'PUT',
        body: { userId: targetUserId, key, value: JSON.stringify(data) },
      })
    },
    // Re-assert the optimistic value after cancelling an in-flight read.
    onMutate: async ({ data, previous, targetUserId }) => {
      const ownerKey = [targetUserId, 'personalization', key]
      await queryClient.cancelQueries({ queryKey: ownerKey })
      queryClient.setQueryData(ownerKey, data)
      return { previous }
    },
    onError: (_error, { targetUserId }, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData([targetUserId, 'personalization', key], context.previous)
      }
    },
  })

  const deleteMutation = useMutation({
    retry: false,
    mutationFn: async (targetUserId: string) => {
      if (targetUserId !== user.value?.userId) {
        throw new Error('Cannot delete metadata: the signed-in user changed')
      }
      await $fetch<null>(metadataUrl, { method: 'DELETE' })
    },
    onMutate: async (targetUserId) => {
      const ownerKey = [targetUserId, 'personalization', key]
      await queryClient.cancelQueries({ queryKey: ownerKey })
      const previous = queryClient.getQueryData<T | null>(ownerKey)
      queryClient.setQueryData(ownerKey, null)
      return { previous }
    },
    onError: (error, targetUserId, context) => {
      const ownerKey = [targetUserId, 'personalization', key]
      if (getErrorStatus(error) === 404) {
        queryClient.setQueryData(ownerKey, null)
        return
      }
      if (context?.previous !== undefined) {
        queryClient.setQueryData(ownerKey, context.previous)
      }
    },
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
    isError: query.isError,
    error: query.error,
    isSuccess: query.isSuccess,
    set: (data: T, options?: { onError?: () => void }) => {
      const targetUserId = user.value?.userId
      if (!isAuthReady.value || !targetUserId) {
        // The write never happens — the optimistic UI would otherwise show
        // saved state, so let the caller react.
        options?.onError?.()
        return
      }
      const previous = queryClient.getQueryData<T | null>(queryKey.value)
      queryClient.setQueryData(queryKey.value, data)
      mutation.mutate({ data, previous, targetUserId }, { onError: options?.onError })
    },
    del: () => {
      const targetUserId = user.value?.userId
      if (!isAuthReady.value || !targetUserId) {
        return
      }
      deleteMutation.mutate(targetUserId)
    },
    isSetting: mutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
